import React, { useState } from 'react';
import Clip from './Clip';
import { OVERLAY_ACCEPTS, formatTimeLabel, sortedClips } from '../state/timelineModel';
import { hasAssetDragOfKind, readAssetDragPayload } from '../state/assetDrag';

/**
 * One layer of the timeline: a labelled lane you drop media onto.
 *
 * The effects lane holds annotations rather than media, so it renders chips
 * instead of clips and takes no drops — an effect is attached to a clip
 * through the inspector, not dragged in from the bin.
 */

/* Effects overlap in time constantly -- a title across a whole shot with a
   one-second fade inside it, two captions in different corners -- and a single
   row draws them all at the same y, so whichever renders last is the only one
   you can see or click. Greedy first-fit lane packing: each effect takes the
   first row free at its start time, and the lane grows a row when none is. */
const EFFECT_ROW_HEIGHT = 22;
const EFFECT_ROW_GAP = 3;

const packIntoRows = (items) => {
    const rowEnds = [];
    const placed = [...items]
        .sort((a, b) => (a.start ?? 0) - (b.start ?? 0))
        .map((item) => {
            const start = item.start ?? 0;
            // A point effect still occupies a chip's worth of lane, so it is
            // given a nominal span rather than colliding with everything at once.
            const end = Math.max(item.end ?? start, start);
            let row = rowEnds.findIndex(rowEnd => start >= rowEnd - 1e-6);
            if (row === -1) {
                row = rowEnds.length;
                rowEnds.push(end);
            } else {
                rowEnds[row] = end;
            }
            return { item, row };
        });
    return { placed, rowCount: Math.max(1, rowEnds.length) };
};

function EffectChip({ effect, row, selected, secondsToPx, onSelect }) {
    const start = effect.start ?? 0;
    const end = effect.end ?? start;
    const isPoint = Math.abs(end - start) < 1e-6;
    const left = secondsToPx(start);
    const width = isPoint ? 10 : Math.max(10, secondsToPx(end - start));
    const label = effect.label || effect.type || effect.tool_name || 'Effect';

    return (
        <div
            className={`nle-effect-chip ${selected ? 'selected' : ''} ${effect.readOnly ? 'read-only' : ''}`}
            style={{
                left,
                width,
                top: EFFECT_ROW_GAP + row * (EFFECT_ROW_HEIGHT + EFFECT_ROW_GAP),
                height: EFFECT_ROW_HEIGHT,
            }}
            onPointerDown={() => onSelect(effect.id)}
            title={effect.readOnly
                ? `${label} — from an AI plan; shown but not editable here`
                : `${label}${isPoint ? '' : ` — ${formatTimeLabel(end - start)}`}`}
            role="button"
            tabIndex={0}
            aria-label={label}
            aria-pressed={selected}
        >
            {width > 44 && <span className="nle-effect-chip-label">{label}</span>}
        </div>
    );
}

function Track({
    layer,
    kind,
    editable,
    contentWidth,
    secondsToPx,
    handleWidth,
    pxToSeconds,
    selectionId,
    onSelect,
    onBeginMove,
    onBeginTrim,
    onDropAsset,
    onScrubToBackground,
    mainEnd = null,
}) {
    const [dropTarget, setDropTarget] = useState(null);
    const isEffects = kind === 'effects';
    const isOverlay = kind === 'overlay';
    // The overlay lane is a video lane in what it takes, not in how it packs.
    const acceptsKind = isOverlay ? OVERLAY_ACCEPTS : kind;
    const items = isEffects ? (layer?.items || []) : sortedClips(layer);
    const effectRows = isEffects ? packIntoRows(items) : null;
    // The lane is exactly as tall as it needs to be: one row when nothing
    // overlaps, growing only when something does.
    const effectsHeight = effectRows
        ? EFFECT_ROW_GAP + effectRows.rowCount * (EFFECT_ROW_HEIGHT + EFFECT_ROW_GAP)
        : undefined;

    /* Where along the track the pointer is, expressed as the clip index the
       dropped asset would take: everything from there on shifts right. */
    const getDropInsertion = (event) => {
        // The track scrolls with the viewport's content, so its own rect
        // already carries the scroll offset — adding it again double-counts.
        const rect = event.currentTarget.getBoundingClientRect();
        const seconds = Math.max(0, pxToSeconds(event.clientX - rect.left));
        // An inset lands exactly where it is dropped; nothing moves for it.
        if (isOverlay) return { index: items.length, seconds, x: secondsToPx(seconds) };
        const index = items.findIndex(item => seconds < (item.start + item.duration / 2));
        const resolved = index < 0 ? items.length : index;
        const edgeSeconds = resolved < items.length
            ? items[resolved].start
            : (items.length > 0 ? items[items.length - 1].end : 0);
        return { index: resolved, x: secondsToPx(edgeSeconds) };
    };

    const handleDragOver = (event) => {
        if (!editable || isEffects || !hasAssetDragOfKind(event, acceptsKind)) return;
        event.preventDefault();
        event.dataTransfer.dropEffect = 'copy';
        const { index, x } = getDropInsertion(event);
        // dragover fires continuously; only a caret that actually moved is
        // worth re-rendering the track for.
        setDropTarget(prev => (prev?.index === index && prev?.x === x ? prev : { index, x }));
    };

    const handleDrop = (event) => {
        setDropTarget(null);
        if (!editable || isEffects) return;
        const payload = readAssetDragPayload(event);
        if (!payload || payload.kind !== acceptsKind) return;
        event.preventDefault();
        const { index, seconds } = getDropInsertion(event);
        onDropAsset(payload.fileName, index, seconds);
    };

    return (
        <div className={`nle-track-row nle-track-${kind}`}>
            <div className="nle-track-label">
                <span className="nle-track-name">{layer?.name || kind}</span>
                <span className="nle-track-count">{items.length}</span>
            </div>

            <div
                className={`nle-track ${dropTarget ? 'drop-active' : ''}`}
                style={{ width: contentWidth, ...(effectsHeight ? { height: effectsHeight } : null) }}
                onDragOver={handleDragOver}
                onDragLeave={() => setDropTarget(null)}
                onDrop={handleDrop}
                // Pressing empty track is a scrub, matching the ruler. It also
                // clears the selection, which is the only way to deselect.
                onPointerDown={(event) => {
                    if (event.target !== event.currentTarget) return;
                    const rect = event.currentTarget.getBoundingClientRect();
                    onSelect(null);
                    onScrubToBackground(pxToSeconds(event.clientX - rect.left));
                }}
            >
                {isEffects && effectRows.placed.map(({ item, row }) => (
                    <EffectChip
                        key={item.id}
                        effect={item}
                        row={row}
                        selected={selectionId === item.id}
                        secondsToPx={secondsToPx}
                        onSelect={onSelect}
                    />
                ))}

                {!isEffects && items.map(item => (
                    <Clip
                        key={item.id}
                        clip={item}
                        kind={kind}
                        selected={selectionId === item.id}
                        editable={editable}
                        secondsToPx={secondsToPx}
                        handleWidth={handleWidth}
                        onSelect={onSelect}
                        onBeginMove={onBeginMove}
                        onBeginTrim={onBeginTrim}
                        cutAt={mainEnd}
                    />
                ))}

                {dropTarget && <div className="nle-drop-caret" style={{ left: dropTarget.x }} />}

                {items.length === 0 && !isEffects && (
                    <span className="nle-track-empty">
                        {!editable ? 'Empty' : isOverlay
                            ? 'Drag a video here to play it over the main video'
                            : `Drag ${kind} here`}
                    </span>
                )}
            </div>
        </div>
    );
}

export default React.memo(Track);
